// src/components/ThreadsDesk.tsx
// 📱 Interfaz EXCLUSIVA para móvil (tipo WebChat)
// - Lista simple → sheet de conversación a pantalla completa
// - Sin preview de último mensaje en la lista (pedido explícito)
// - Urgentes MUY visibles (borde/halo/etiqueta y acento izquierdo)
// - Filtros SOLO por prioridad (Urgentes / Revisión / Neutras)
// - Búsqueda con debounce
// - Normalización de BLOQUES (nada de JSON crudo)
// - Usa SDK central: getThreads / getConversations (lib/api)

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence, PanInfo } from "framer-motion";
import {
  MessageSquare,
  Loader2 as Loader,
  RefreshCw,
  Filter,
  Phone,
  Globe,
  MessageCircle,
  Facebook,
  Instagram,
  Mic,
  Mail,
  Cpu,
  HelpCircle,
  User,
  Bot,
  Search,
  X,
  CalendarClock,
  Play,
  Pause,
  ChevronLeft,
  UserCheck,
  LayoutDashboard,
} from "lucide-react";

// SDK / API
import { getThreads, getMyThreads, getConversations, sendHumanMessage, assignThreadOwner, type ThreadDTO } from "@/lib/api";
import { useModules } from "@/context/ModulesContext";
import { listSellers, type OrgUser } from "@/modules/sales-desk/api";
import { AgentControl } from "@/components/AgentControl";
import ConversationsInbox from "@/components/conversations/ConversationsInbox";

/* ========================= Tipos locales ========================= */
export type Thread = {
  id: string;
  session_id: string;
  telefono: string;
  status: "open" | "waiting_admin" | "waiting_user" | "scheduled" | "closed" | string;
  priority: "urgent" | "atender" | "neutral" | string;
  last_message_at: string;
  last_message_preview?: string | null; // (no se usa en UI de lista)
  last_preview_raw?: string | null;     // raw del DTO para derivar prioridad con bloques
  last_sentiment?: string | null;
  next_appointment_start?: string | null;
  owner_user_id?: string | null;
  unread_admin_count?: number | null;
  channel: string;   // de dónde viene (ver CHANNEL_META)
  party: string;     // external | internal | test | unknown
};

/* ========================= Canal de la conversación ========================= */
// Orión clasifica cada hilo (db/conversation_channel.py). Aquí solo se muestra.
type ChannelMeta = { label: string; Icon: React.ComponentType<{ className?: string }>; cls: string };
const CHANNEL_META: Record<string, ChannelMeta> = {
  whatsapp:       { label: "WhatsApp",  Icon: MessageCircle, cls: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" },
  facebook:       { label: "Messenger", Icon: Facebook,      cls: "text-blue-500 bg-blue-500/10 border-blue-500/30" },
  instagram:      { label: "Instagram", Icon: Instagram,     cls: "text-pink-500 bg-pink-500/10 border-pink-500/30" },
  panel:          { label: "Panel",     Icon: LayoutDashboard, cls: "text-indigo-500 bg-indigo-500/10 border-indigo-500/30" },
  web:            { label: "Web",       Icon: Globe,         cls: "text-sky-500 bg-sky-500/10 border-sky-500/30" },
  voice_realtime: { label: "Voz web",   Icon: Mic,           cls: "text-violet-500 bg-violet-500/10 border-violet-500/30" },
  voice_phone:    { label: "Llamada",   Icon: Phone,         cls: "text-violet-500 bg-violet-500/10 border-violet-500/30" },
  email:          { label: "Correo",    Icon: Mail,          cls: "text-amber-500 bg-amber-500/10 border-amber-500/30" },
  api:            { label: "Sistema",   Icon: Cpu,           cls: "text-muted-foreground bg-muted border-border" },
  unknown:        { label: "Otro",      Icon: HelpCircle,    cls: "text-muted-foreground bg-muted border-border" },
};
const channelMeta = (c?: string | null): ChannelMeta => CHANNEL_META[c || ""] || CHANNEL_META.unknown;
const CHANNEL_ORDER = ["panel", "whatsapp", "facebook", "instagram", "web", "voice_phone", "voice_realtime", "email", "api", "unknown"];

// Respaldo mientras un backend viejo no mande `channel`: el prefijo de sesión
// lo pone el propio gateway, así que basta para no mostrar todo como «Otro».
function inferChannel(sessionId: string): string {
  if (sessionId.startsWith("panel:")) return "panel";
  if (sessionId.startsWith("fb:")) return "facebook";
  if (sessionId.startsWith("ig:")) return "instagram";
  if (sessionId.startsWith("web:")) return "web";
  if (sessionId.startsWith("rt:")) return "voice_realtime";
  return "unknown";
}

function ChannelPill({ channel, party }: { channel: string; party?: string }) {
  const m = channelMeta(channel);
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium inline-flex items-center gap-1 ${m.cls}`}>
        <m.Icon className="w-3 h-3" /> {m.label}
      </span>
      {party === "internal" && (
        <span className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground">Interno</span>
      )}
      {party === "test" && (
        <span className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground">Prueba</span>
      )}
    </span>
  );
}

export type Bloque =
  | { tipo: "texto"; contenido: string }
  | { tipo: "imagen"; contenido: string }
  | { tipo: "video"; contenido: string }
  | { tipo: "audio"; contenido: string }
  | { tipo: "pdf"; contenido: string };

export type ConversationLog = {
  role: "user" | "assistant";
  message: string;
  timestamp: string | number;
  bloques?: Bloque[];
  telefono?: string;
};

/* ========================= Helpers ========================= */
const MX = { timeZone: "America/Mexico_City" as const };

// Fecha + hora en horario de México (America/Mexico_City). Si el timestamp viene
// SIN zona horaria (naive), se asume UTC — así lo persiste el backend — para no
// desfasar. Muestra día y hora → se ve CUÁNDO escribieron.
export function mxStamp(t: string | number): string {
  let d: Date;
  if (typeof t === "number") {
    d = new Date(t);
  } else {
    const s = String(t || "");
    const hasTZ = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
    d = new Date(s ? (hasTZ ? s : s + "Z") : Date.now());
  }
  return d.toLocaleString("es-MX", {
    timeZone: "America/Mexico_City",
    day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit",
  });
}

// Normaliza (minúsculas sin acentos) para matching robusto
const fold = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const URGENT_KWS = [
  "urgente","urgent","emergencia","emergency","auxilio","ayuda",
  "asap","urg","prioridad alta","alta prioridad","high priority",
  "prio_alta","alta","inmediato","inmediata","critico","crítico","critica","crítica","soporte"
];
const REVIEW_KWS = ["revisión","revision","review","pendiente","validar","confirmar","verificar","checar"];
const URGENT_KWS_F = URGENT_KWS.map(fold);
const REVIEW_KWS_F = REVIEW_KWS.map(fold);

const normalizePriority = (p?: string | null): "urgent" | "atender" | "neutral" => {
  const s = String(p ?? "").toLowerCase().trim();
  if (["urgent","urgente","alta","high","prio_alta"].includes(s)) return "urgent";
  if (["atender","revision","revisión","review","pendiente"].includes(s)) return "atender";
  return "neutral";
};

export const derivePriority = (t: Thread | ThreadDTO): "urgent" | "atender" | "neutral" => {
  // 1) Respeta backend si lo manda
  const explicit = normalizePriority((t as any).priority);
  if (explicit !== "neutral") return explicit;

  // 2) Recolecta textos
  const texts: string[] = [];
  const dtoRaw = (t as any).last_message_preview;
  const mappedRaw = (t as any).last_preview_raw;
  const sanitized = (t as any).last_message_preview;

  const push = (v?: unknown) => { if (typeof v === "string" && v.trim()) texts.push(v); };
  push(dtoRaw); push(mappedRaw); push(sanitized);

  // Intenta extraer bloques si hay JSON
  [dtoRaw, mappedRaw].forEach((raw) => {
    if (typeof raw === "string" && raw.trim().startsWith("{")) {
      try {
        const bs = toBloques(raw);
        bs.forEach((b) => b.tipo === "texto" && push(b.contenido));
      } catch {}
    }
  });

  const hay = fold(texts.join(" • "));
  const includesAny = (arr: string[]) => arr.some((kw) => hay.includes(kw));
  const hasPrioridadAlta = hay.includes("prioridad") && hay.includes("alta");

  if (includesAny(URGENT_KWS_F) || hasPrioridadAlta) return "urgent";
  if (includesAny(REVIEW_KWS_F)) return "atender";
  return "neutral";
};

const normalizeStatus = (s?: string | null): Thread["status"] => {
  const v = String(s ?? "").toLowerCase().trim();
  if (["open","waiting_admin","waiting_user","scheduled","closed"].includes(v)) return v as any;
  return "open";
};

const isOpenLike = (s?: string | null) => {
  const v = normalizeStatus(s);
  return v === "open" || v === "waiting_admin" || v === "waiting_user" || v === "scheduled";
};

const safeTrunc = (s: string, n = 180) => (s.length > n ? s.slice(0, n - 1).trimEnd() + "…" : s);

export function toBloques(input: unknown): Bloque[] {
  // Acepta string cruda, JSON serializado, arreglo de bloques o objeto con bloques
  try {
    if (typeof input === "string") {
      const s = input.trim();
      if (!s) return [];
      if (s.startsWith("{")) {
        const obj = JSON.parse(s);
        return toBloques(obj);
      }
      return [{ tipo: "texto", contenido: s }];
    }
    if (Array.isArray(input)) {
      const arr = input
        .map((it) => (typeof it === "string" ? { tipo: "texto", contenido: it } : it))
        .filter((b: any) => b && typeof b === "object" && typeof (b as any).tipo === "string")
        .map((b: any) => ({ tipo: b.tipo, contenido: String(b.contenido ?? "") })) as Bloque[];
      return arr.filter((b) => b.contenido !== "");
    }
    if (typeof input === "object" && input) {
      const anyObj: any = input as any;
      if (Array.isArray(anyObj.bloques)) {
        return anyObj.bloques
          .filter((b: any) => b && typeof b === "object" && typeof b.tipo === "string")
          .map((b: any) => ({ tipo: b.tipo, contenido: String(b.contenido ?? "") }))
          .filter((b: Bloque) => b.contenido !== "");
      }
      if (typeof anyObj.respuesta === "string") return [{ tipo: "texto", contenido: anyObj.respuesta }];
      if (typeof anyObj.summary === "string") return [{ tipo: "texto", contenido: anyObj.summary }];
      if (typeof anyObj.resumen === "string") return [{ tipo: "texto", contenido: anyObj.resumen }];
    }
  } catch {}
  return [];
}

export function extractAssistantPreview(raw?: string | null): string {
  const s = (raw ?? "").trim();
  if (!s) return "";

  // Caso 1: JSON válido
  if (s.startsWith("{")) {
    try {
      const obj: any = JSON.parse(s);
      const bloques = toBloques(obj);
      const textBlock = Array.isArray(bloques) ? bloques.find((b) => b.tipo === "texto") : undefined;
      if (textBlock) return safeTrunc(textBlock.contenido, 160);

      const txt = (obj?.respuesta ?? obj?.resumen ?? obj?.summary) as unknown;
      if (typeof txt === "string" && txt.trim()) return safeTrunc(txt.trim(), 160);
      return "[Respuesta interactiva]";
    } catch {}
  }

  // Caso 2: JSON truncado con claves conocidas
  if (s.includes("bloques") || s.includes("contenido") || s.includes("modo_respuesta")) {
    const m1 = s.match(/"contenido"\s*:\s*"([^"\\]{1,400}(?:\\.[^"\\]{0,400})*)"/m);
    if (m1 && m1[1]) {
      const candidate = m1[1].replace(/\\n/g, " ").replace(/\\"/g, '"').trim();
      if (candidate) return safeTrunc(candidate, 160);
    }
    const m2 = s.match(/"(respuesta|resumen|summary)"\s*:\s*"([^"\\]{1,400}(?:\\.[^"\\]{0,400})*)"/m);
    if (m2 && m2[2]) {
      const candidate2 = m2[2].replace(/\\n/g, " ").replace(/\\"/g, '"').trim();
      if (candidate2) return safeTrunc(candidate2, 160);
    }
    return "[Respuesta interactiva]";
  }

  // Caso 3: texto plano
  return safeTrunc(s, 160);
}

export const mapDto = (t: ThreadDTO): Thread => ({
  id: String(t.id),
  session_id: String(t.session_id),
  telefono: String(t.telefono),
  status: normalizeStatus((t as any).status),
  priority: normalizePriority((t as any).priority),
  last_message_at: String((t as any).last_message_at || new Date().toISOString()),
  last_message_preview: extractAssistantPreview((t as any).last_message_preview),
  last_preview_raw: typeof (t as any).last_message_preview === "string" ? (t as any).last_message_preview : null,
  last_sentiment: (t as any).last_sentiment ?? null,
  next_appointment_start: (t as any).next_appointment_start ?? null,
  owner_user_id: (t as any).owner_user_id ?? null,
  unread_admin_count: (t as any).unread_admin_count ?? 0,
  channel: (t.channel && t.channel !== "unknown") ? t.channel : inferChannel(String(t.session_id)),
  party: t.party || "unknown",
});

/* ========================= Hooks utilitarios ========================= */
function useDebounced<T>(value: T, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

function useLockBody(locked: boolean) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (locked) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
}

/* ========================= UI (Mobile-only) ========================= */

type Estado = "all" | "open_like" | "open" | "waiting_admin" | "waiting_user" | "scheduled" | "closed";
type Categoria = "all" | "urgente" | "revision" | "neutra";

/**
 * Bandeja de conversaciones. En modo admin usa la bandeja v1 (Clientes /
 * Equipo / Pruebas, GET /threads/inbox); si el backend todavía no la tiene,
 * cae sola a la bandeja anterior (GET /threads/), que sigue intacta abajo.
 */
export default function ThreadsDesk(props: { mode?: "admin" | "personal"; apiBase?: string }) {
  const [inboxV1, setInboxV1] = useState(true);
  const unavailable = useCallback(() => setInboxV1(false), []);
  if ((props.mode ?? "admin") === "admin" && inboxV1) {
    return <ConversationsInbox onUnavailable={unavailable} />;
  }
  return <LegacyThreadsDesk {...props} />;
}

function LegacyThreadsDesk({ mode = "admin", apiBase }: { mode?: "admin" | "personal"; apiBase?: string }) {
  // Datos
  const [rawThreads, setRawThreads] = useState<Thread[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "error" | "success">("idle");
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [q, setQ] = useState("");
  const qDeb = useDebounced(q, 350);
  const [estado, setEstado] = useState<Estado>("open_like"); // se mantiene interno
  const [cat, setCat] = useState<Categoria>("all");
  const [canal, setCanal] = useState<string>("all"); // filtro por canal (cliente)

  // Selección
  const [active, setActive] = useState<Thread | null>(null);

  // 👥 Asignación a vendedoras. El SERVIDOR decide qué ve cada quien: a una
  // vendedora /threads le devuelve SOLO las conversaciones asignadas a ella.
  // Aquí solo se adapta la UI: el admin asigna, la vendedora ve lo suyo.
  const { isAdmin, userRoles } = useModules();
  const esDev = !!userRoles?.some((r) => r === "cto" || r === "developer");
  const esVendedora = !isAdmin && !esDev && !!userRoles?.some((r) => ["vendedor", "socio", "partner"].includes(r));
  const puedeAsignar = mode === "admin" && (isAdmin || esDev);
  const [sellers, setSellers] = useState<OrgUser[]>([]);
  useEffect(() => {
    if (!puedeAsignar) return;
    listSellers().then((u) => setSellers(Array.isArray(u) ? u : [])).catch(() => setSellers([]));
  }, [puedeAsignar]);
  const nombreDe = useCallback((id?: string | null) => {
    if (!id) return null;
    const u = sellers.find((s) => s.user_id === id);
    return u ? (u.full_name || u.username) : null;
  }, [sellers]);
  const handleAssigned = useCallback((threadId: string, ownerId: string | null) => {
    setRawThreads((prev) => prev.map((x) => (x.id === threadId ? { ...x, owner_user_id: ownerId } : x)));
    setActive((a) => (a && a.id === threadId ? { ...a, owner_user_id: ownerId } : a));
  }, []);
  const [messages, setMessages] = useState<ConversationLog[]>([]);
  const [msgsStatus, setMsgsStatus] = useState<"idle" | "loading" | "success">("idle");

  const reloadThreads = useCallback(async (signal?: AbortSignal) => {
    setStatus("loading");
    setError(null);
    try {
      let rows: any[] = [];
      if (mode === "personal") {
        const res = await getMyThreads();
        rows = (res.threads || []).map((t: any) => ({
             ...t,
             id: t.thread_id, 
             priority: "neutral"
        }));
      } else {
        const params: any = { limit: 400, offset: 0 };
        if (estado !== "all" && estado !== "open_like") params.status = estado;
        // ⚠️ Importante: NO enviar priority. Filtramos en cliente con derivePriority()
        if (qDeb.trim()) params.search = qDeb.trim();

        rows = await getThreads(params, { signal } as any);
      }
      setRawThreads(rows.map(mapDto));
      setStatus("success");
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      setStatus("error");
      setError(e?.message || "Error al listar hilos");
    }


  }, [estado, qDeb, mode]);

  useEffect(() => {
    const ac = new AbortController();
    reloadThreads(ac.signal);
    return () => ac.abort();
  }, [reloadThreads]);

  const threads = useMemo(() => {
    let arr = rawThreads.slice();
    if (qDeb.trim()) {
      const t = fold(qDeb.trim());
      arr = arr.filter(
        (x) => fold(x.telefono).includes(t) || fold(x.last_message_preview || "").includes(t)
      );
    }
    if (cat !== "all") {
      arr = arr.filter((x) => {
        const c = derivePriority(x);
        return (
          (cat === "urgente" && c === "urgent") ||
          (cat === "revision" && c === "atender") ||
          (cat === "neutra" && c === "neutral")
        );
      });
    }
    if (estado !== "all") {
      if (estado === "open_like") arr = arr.filter((x) => isOpenLike(x.status));
      else arr = arr.filter((x) => normalizeStatus(x.status) === estado);
    }
    if (canal !== "all") arr = arr.filter((x) => x.channel === canal);
    arr.sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime());
    return arr;
  }, [rawThreads, qDeb, cat, estado, canal]);

  // Canales presentes en la bandeja (solo se muestran chips de los que hay).
  const channelCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of rawThreads) m[t.channel] = (m[t.channel] || 0) + 1;
    return CHANNEL_ORDER.filter((c) => m[c]).map((c) => ({ c, n: m[c] }));
  }, [rawThreads]);

  const counts = useMemo(() => {
    const all = rawThreads.length;
    const byStatus = {
      all,
      open_like: rawThreads.filter((x) => isOpenLike(x.status)).length,
      open: rawThreads.filter((x) => normalizeStatus(x.status) === "open").length,
      waiting_admin: rawThreads.filter((x) => normalizeStatus(x.status) === "waiting_admin").length,
      waiting_user: rawThreads.filter((x) => normalizeStatus(x.status) === "waiting_user").length,
      scheduled: rawThreads.filter((x) => normalizeStatus(x.status) === "scheduled").length,
      closed: rawThreads.filter((x) => normalizeStatus(x.status) === "closed").length,
    };
    const u = rawThreads.filter((x) => derivePriority(x) === "urgent").length;
    const r = rawThreads.filter((x) => derivePriority(x) === "atender").length;
    const n = all - u - r;
    return { byStatus, all, u, r, n };
  }, [rawThreads]);

  // Mensajes del hilo activo
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!active) return;
      setMsgsStatus("loading");
      try {
        const rows: any[] = await getConversations(undefined, active.session_id);
        if (cancelled) return;
        const logs = (rows || []).map((r: any) => {
          const raw = String(r.message ?? r.text ?? "");
          // Filtramos solo bloques permitidos para el usuario final
          const sysTypes = ["texto", "imagen", "video", "audio", "pdf"];
          const bloques = toBloques(raw).filter((b) => sysTypes.includes(b.tipo) && !b.contenido.includes("[INTERNAL]"));
          
          return {
            role: r.role === "assistant" ? "assistant" : "user",
            message: bloques.length ? "" : raw,
            timestamp: r.timestamp ?? r.at ?? r.created_at ?? Date.now(),
            bloques,
            telefono: r.telefono,
          } as ConversationLog;
        });
        setMessages(logs);
      } finally {
        if (!cancelled) setMsgsStatus("success");
      }
    })();
    return () => { cancelled = true; };
  }, [active]);

  const handleRefresh = () => {
    const ac = new AbortController();
    reloadThreads(ac.signal);
    setTimeout(() => ac.abort(), 8000);
  };

  const [filtersOpen, setFiltersOpen] = useState(false);

  // Bloquea scroll del body cuando hay sheet o filtros abiertos
  useLockBody(!!active || filtersOpen);

  /* ========================= Render (Mobile) ========================= */
  return (
    <div className="h-[calc(100dvh-7rem)] w-full flex flex-col min-h-0 bg-[radial-gradient(1200px_400px_at_10%_-10%,rgba(160,160,255,.08),transparent),radial-gradient(1000px_300px_at_110%_10%,rgba(120,220,255,.06),transparent)]">
      {/* Header sticky */}
      <div className="o-sticky-head sticky top-0 z-20">
        <div className="px-3 py-2 flex items-center gap-2">
          <div className="p-2 rounded-xl bg-muted border border-border shadow-inner">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div className="font-semibold text-gradient text-base">
            {mode === "personal" ? "Mis Conversaciones" : esVendedora ? "Mis conversaciones asignadas" : "Bandeja de Entrada"}
          </div>
          <div className="ml-auto flex items-center gap-1">
            <button className="o-icon-btn" title="Refrescar" aria-label="Refrescar" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4" />
            </button>
            {mode === "admin" && (
              <button className="o-icon-btn" title="Filtros" aria-label="Filtros" onClick={() => setFiltersOpen(true)}>
                <Filter className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {/* Search inline */}
        <div className="px-3 pb-2">
          <div className="flex items-center gap-2 px-2 py-2 rounded-xl bg-muted/50 border border-border">
            <Search className="w-4 h-4 opacity-70" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar teléfono o texto…"
              className="bg-transparent w-full outline-none placeholder:opacity-60 text-[15px]"
              aria-label="Buscar"
            />
            {q && (
              <button onClick={() => setQ("")} className="opacity-70 hover:opacity-100" aria-label="Limpiar búsqueda">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {/* Chips por canal: de dónde viene cada conversación */}
        {channelCounts.length > 1 && (
          <div className="px-3 pb-2 overflow-x-auto no-scrollbar">
            <div className="flex gap-2 min-w-max" role="group" aria-label="Filtrar por canal">
              <button
                onClick={() => setCanal("all")}
                className={`text-[12px] px-2.5 py-1 rounded-full border ${canal === "all" ? "bg-primary/10 border-primary/40 text-foreground" : "border-border text-muted-foreground"}`}
              >
                Todos ({rawThreads.length})
              </button>
              {channelCounts.map(({ c, n }) => {
                const m = channelMeta(c);
                return (
                  <button
                    key={c}
                    onClick={() => setCanal(canal === c ? "all" : c)}
                    className={`text-[12px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${canal === c ? m.cls : "border-border text-muted-foreground"}`}
                  >
                    <m.Icon className="w-3.5 h-3.5" /> {m.label} ({n})
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {/* Chips horizontales – SOLO prioridad (Solo Admin) */}
        {mode === "admin" && (
          <div className="px-3 pb-2 overflow-x-auto no-scrollbar">
            <div className="flex gap-2 min-w-max">
              <Chip active={cat === "urgente"} onClick={() => setCat("urgente")} tone="urgente">
                Urgentes ({counts.u})
              </Chip>
              <Chip active={cat === "revision"} onClick={() => setCat("revision")} tone="revision">
                Revisión ({counts.r})
              </Chip>
              <Chip active={cat === "neutra"} onClick={() => setCat("neutra")} tone="neutra">
                Neutras ({counts.n})
              </Chip>
            </div>
          </div>
        )}
      </div>

      {/* Lista */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-[calc(12px+env(safe-area-inset-bottom))]">
        {status === "loading" && (
          <div className="space-y-2 pt-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-20 rounded-2xl bg-muted/50 border border-border animate-pulse" />
            ))}
          </div>
        )}

        {status === "error" && (
          <div className="o-card bg-red-500/10 border border-red-400/30 text-red-100 text-sm p-3 flex items-start gap-2 mt-2">
            <X className="w-4 h-4 mt-0.5" />
            <div>
              <div className="font-semibold">Error</div>
              <div className="opacity-90 break-words">{error}</div>
            </div>
          </div>
        )}

        {status === "success" && threads.length === 0 && (
          <div className="opacity-70 text-sm mt-3">
            {esVendedora
              ? "Aún no tienes conversaciones asignadas. Cuando la administración te asigne una, aparecerá aquí."
              : "No hay conversaciones."}
          </div>
        )}

        {status === "success" && (
          <div className="space-y-2 pt-2">
            {threads.map((t) => (
              <MobileThreadRow key={t.id} thread={t} ownerName={puedeAsignar ? nombreDe(t.owner_user_id) : null} onClick={() => setActive(t)} />
            ))}
          </div>
        )}
      </div>

      {/* Sheet de conversación a pantalla completa */}
      <ChatSheet
        open={!!active}
        onClose={() => setActive(null)}
        thread={active}
        messages={messages}
        status={msgsStatus}
        sellers={sellers}
        canAssign={puedeAsignar}
        onAssigned={handleAssigned}
      />

      {/* Modal de filtros (solo prioridad) */}
      <FilterSheet
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        counts={counts}
        cat={cat}
        setCat={setCat}
      />
    </div>
  );
}

/* ========================= Subcomponentes (móvil) ========================= */

function MobileThreadRow({ thread, ownerName, onClick }: { thread: Thread; ownerName?: string | null; onClick: () => void }) {
  const p = derivePriority(thread);
  const cat = p === "urgent" ? "urgente" : p === "atender" ? "revision" : "neutra";
  const isUrgent = p === "urgent";
  const lastAt = new Date(thread.last_message_at);
  const ch = channelMeta(thread.channel);

  const rowCls = [
    "w-full text-left rounded-2xl p-3 border active:scale-[.995] transition-transform relative",
    isUrgent ? "bg-rose-400/10 border-rose-400/40 ring-2 ring-rose-400/30" : "bg-muted/50 border-border",
  ].join(" ");

  return (
    <button onClick={onClick} className={rowCls} aria-label={`Abrir conversación con ${thread.telefono}`}>
      {/* Acento lateral para urgentes */}
      <span
        aria-hidden
        className={[
          "absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl",
          isUrgent ? "bg-rose-400/80" : "bg-transparent",
        ].join(" ")}
      />

      <div className="flex items-start gap-3">
        <div className={["p-2 rounded-xl border", isUrgent ? "bg-rose-400/15 border-rose-400/40" : "bg-muted border-border"].join(" ")}>
          <ch.Icon className={`w-4 h-4 ${ch.cls.split(" ")[0]}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-semibold truncate text-[15px]">{thread.telefono}</div>
            <ChannelPill channel={thread.channel} party={thread.party} />
            {isUrgent && (
              <span className="ml-1 inline-flex items-center gap-1 text-[11px] text-rose-300">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" /> URGENTE
              </span>
            )}
            {thread.next_appointment_start && (
              <div className="flex items-center gap-1 text-[11px] opacity-80">
                <CalendarClock className="w-3 h-3" />
                <span>
                  {mxStamp(thread.next_appointment_start)}
                </span>
              </div>
            )}
            <div className="ml-auto text-xs opacity-60 flex-shrink-0">
              {mxStamp(thread.last_message_at)}
            </div>
          </div>
          {/* 👇 Sin preview por petición */}
          <div className="flex items-center gap-2 mt-1">
            <Badge tone={cat as any}>{cat}</Badge>
            <Badge muted>{normalizeStatus(thread.status)}</Badge>
            {!!thread.unread_admin_count && <Badge glow>{thread.unread_admin_count} nuevos</Badge>}
            {ownerName && <Badge muted>Asignada a {ownerName}</Badge>}
          </div>
        </div>
      </div>
    </button>
  );
}

function Badge({ children, tone, muted, glow }: { children: React.ReactNode; tone?: "urgente" | "revision" | "neutra"; muted?: boolean; glow?: boolean }) {
  const base = "text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1";
  const toneCls =
    tone === "urgente"
      ? "text-rose-300 border-rose-400/40"
      : tone === "revision"
      ? "text-amber-300 border-amber-400/40"
      : tone === "neutra"
      ? "text-muted-foreground border-slate-400/40"
      : "text-muted-foreground border-border";
  const mutedCls = muted ? "opacity-80" : "";
  const glowCls = glow ? "shadow-[0_0_14px_rgba(255,255,255,.18)]" : "";
  return <span className={[base, toneCls, mutedCls, glowCls].join(" ")}>{children}</span>;
}

export function ChatSheet({
  open,
  onClose,
  thread,
  messages,
  status,
  sellers = [],
  canAssign = false,
  onAssigned,
  readOnly = false,
  title,
}: {
  open: boolean;
  onClose: () => void;
  thread: Thread | null;
  messages: ConversationLog[];
  status: "idle" | "loading" | "success";
  sellers?: OrgUser[];
  canAssign?: boolean;
  onAssigned?: (threadId: string, ownerId: string | null) => void;
  /** Conversación del equipo con el agente interno: solo lectura (auditoría). */
  readOnly?: boolean;
  title?: string | null;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  // 🙋 Takeover: mensajes que el staff envía al ciudadano. Append optimista local
  // (el padre carga `messages` por hilo; aquí se muestra lo enviado al instante).
  const [localSent, setLocalSent] = useState<ConversationLog[]>([]);
  const [sending, setSending] = useState(false);
  useEffect(() => { setLocalSent([]); }, [thread?.id]); // reset al cambiar de conversación

  const handleSend = useCallback(async (t: string) => {
    const text = (t || "").trim();
    if (!thread || !text || sending) return;
    const optimistic = {
      role: "assistant", message: text, timestamp: new Date().toISOString(),
      author_type: "admin", bloques: [],
    } as unknown as ConversationLog;
    setLocalSent((prev: ConversationLog[]) => [...prev, optimistic]);
    setSending(true);
    try {
      await sendHumanMessage(thread.id, text);
    } catch {
      setLocalSent((prev: ConversationLog[]) => prev.map((m: ConversationLog) => m === optimistic
        ? ({ ...m, message: text + "  ⚠️ (no se pudo enviar)" } as ConversationLog) : m));
    } finally {
      setSending(false);
    }
  }, [thread, sending]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, localSent, open]);

  const isUrgent = thread ? derivePriority(thread) === "urgent" : false;

  return (
    <AnimatePresence>
      {open && thread && (
        <motion.div
          // justify-end: el panel (alto = pantalla − 7rem) queda abajo y deja libre la barra
          // superior de la app. Antes se pegaba arriba y esa barra tapaba el encabezado, con
          // el botón «Tomar control» incluido (Mountain360, 22-oct-2026).
          className="fixed inset-0 z-50 flex flex-col justify-end bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative h-[calc(100dvh-7rem)] max-h-[calc(100dvh-7rem)] bg-background border-t border-border flex flex-col"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            {/* Header tipo WebChat */}
            <div className="o-sticky-head sticky top-0 z-10 px-3 py-2 flex items-center gap-2">
              <button onClick={onClose} aria-label="Volver" className="o-icon-btn">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="p-2 rounded-xl bg-muted border border-border shadow-inner">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div className="flex flex-col leading-tight min-w-0">
                <span className="font-semibold text-gradient truncate">{title || thread.telefono}</span>
                <span className="text-[11px] opacity-70 truncate">
                  {channelMeta(thread.channel).label} · Estado: {normalizeStatus(thread.status)}
                </span>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {!readOnly && (
                  <AgentControl
                    conv={{ thread_id: thread.id, session_id: thread.session_id, telefono: thread.telefono }}
                    autoLoad
                  />
                )}
                {isUrgent && <span className="text-[11px] px-2 py-1 rounded-full border border-rose-400/60 text-rose-300">URGENTE</span>}
                {thread.next_appointment_start && (
                  <span className="text-[11px] px-2 py-1 rounded-full border border-border text-muted-foreground inline-flex items-center gap-1">
                    <CalendarClock className="w-3 h-3" />
                    {mxStamp(thread.next_appointment_start)}
                  </span>
                )}
              </div>
            </div>

            {canAssign && sellers.length > 0 && (
              <AssignBar thread={thread} sellers={sellers} onAssigned={onAssigned} />
            )}

            {/* Mensajes */}
            <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-4">
              {status === "loading" && (
                <div className="flex items-center gap-2 text-sm opacity-90 pt-2">
                  <Loader className="w-4 h-4 animate-spin" /> Cargando…
                </div>
              )}
              {status !== "loading" && messages.length === 0 && localSent.length === 0 && (
                <div className="o-card border-border bg-muted/50 text-sm p-4">No hay mensajes en este hilo todavía.</div>
              )}
              {[...messages, ...localSent].map((m, i) => (
                <MessageBubble key={`${m.timestamp}_${i}`} msg={m} />
              ))}
            </div>

            {/* Input (no en conversaciones del equipo: se auditan, no se contestan) */}
            {readOnly ? (
              <div className="px-3 pb-[calc(10px+env(safe-area-inset-bottom))] pt-2 text-[12px] text-muted-foreground">
                Conversación privada del equipo con el agente interno · solo lectura.
              </div>
            ) : (
              <div className="px-3 pb-[calc(10px+env(safe-area-inset-bottom))] pt-1">
                <ChatInputBar onSend={handleSend} />
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Admin: asigna la conversación a una vendedora. Ella la verá en "Mis
 *  conversaciones" y podrá tomar el control y contestar. "Sin asignar" la quita. */
function AssignBar({
  thread,
  sellers,
  onAssigned,
}: {
  thread: Thread;
  sellers: OrgUser[];
  onAssigned?: (threadId: string, ownerId: string | null) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const value = thread.owner_user_id || "";
  const conocida = !value || sellers.some((s) => s.user_id === value);

  const onChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value || "";
    setSaving(true);
    setErr(null);
    try {
      await assignThreadOwner(thread.id, next);
      onAssigned?.(thread.id, next || null);
    } catch (x: any) {
      setErr(x?.message || "No se pudo asignar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-3 py-2 border-b border-border flex items-center gap-2 text-sm">
      <UserCheck className="w-4 h-4 opacity-70 flex-shrink-0" />
      <span className="opacity-80 flex-shrink-0">Asignada a</span>
      <select
        value={value}
        onChange={onChange}
        disabled={saving}
        aria-label="Asignar conversación a una vendedora"
        className="flex-1 min-w-0 rounded-lg bg-muted border border-border px-2 py-1 text-foreground"
      >
        <option value="">Sin asignar</option>
        {sellers.map((s) => (
          <option key={s.user_id} value={s.user_id}>{s.full_name || s.username}</option>
        ))}
        {!conocida && <option value={value}>Otro usuario</option>}
      </select>
      {saving && <Loader className="w-4 h-4 animate-spin flex-shrink-0" />}
      {err && <span className="text-rose-400 text-xs">{err}</span>}
    </div>
  );
}

function MessageBubble({ msg }: { msg: ConversationLog }) {
  const isUser = msg.role === "user";
  return (
    <div className={`w-full flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[90%] rounded-2xl px-3 py-2",
          "border backdrop-blur-md shadow-lg transition",
          isUser ? "bg-amber-500/10 border-amber-500/30 text-foreground" : "bg-indigo-500/10 border-indigo-500/30 text-foreground",
        ].join(" ")}
      >
        <div className="flex items-center gap-2 text-[11px] opacity-80 mb-1">
          <div className={`p-1 rounded-md ${isUser ? "bg-amber-500/20" : "bg-indigo-500/20"}`}>
            {isUser ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
          </div>
          <span>{isUser ? "Tú" : "Asistente"}</span>
          <span className="opacity-60">•</span>
          <span className="opacity-70">{mxStamp(msg.timestamp)}</span>
        </div>
        <div className="space-y-2">
          {Array.isArray(msg.bloques) && msg.bloques.length > 0 ? (
            msg.bloques.map((b, idx) => <BloqueRenderer key={`b_${idx}`} bloque={b} />)
          ) : (
            <p className="whitespace-pre-wrap break-words leading-relaxed text-[14px]">{msg.message}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function BloqueRenderer({ bloque }: { bloque: Bloque }) {
  switch (bloque.tipo) {
    case "texto":
      return <p className="whitespace-pre-wrap break-words leading-relaxed text-[14px]">{bloque.contenido}</p>;
    case "imagen":
      // eslint-disable-next-line @next/next/no-img-element
      return (
        <img
          src={bloque.contenido}
          alt="imagen"
          loading="lazy"
          className="rounded-lg border border-border shadow max-w-full"
        />
      );
    case "video":
      return (
        <video
          controls
          playsInline
          preload="metadata"
          src={bloque.contenido}
          className="rounded-lg border border-border shadow w-full max-h-[42vh] bg-muted"
        >
          Tu navegador no soporta video.
        </video>
      );
    case "audio":
      return <AudioPlayer src={bloque.contenido} />;
    case "pdf":
      return (
        <a href={bloque.contenido} target="_blank" rel="noopener noreferrer" className="underline text-gradient">
          📄 Ver documento PDF
        </a>
      );
    default:
      return <p className="text-xs italic opacity-70">[bloque no reconocido]</p>;
  }
}

function AudioPlayer({ src }: { src: string }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      <audio ref={ref} src={src} onEnded={() => setPlaying(false)} className="hidden" />
      <button onClick={toggle} className="o-btn-ghost p-2 hover:shadow-none hover:translate-y-0" aria-label="Reproducir/Pausar">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <span className="text-sm opacity-80">Audio</span>
    </div>
  );
}

function ChatInputBar({ onSend }: { onSend: (text: string) => void }) {
  const [input, setInput] = useState("");
  const handleSend = () => {
    const v = input.trim();
    if (!v) return;
    onSend(v);
    setInput("");
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="o-chat-input-wrap">
      <div className="o-chat-input">
        {/* Botón decorativo clip */}
        <button title="Adjuntar" aria-label="Adjuntar" className="o-clip-btn">
          <svg className="o-clip-svg" viewBox="0 0 24 24" fill="none" width="24" height="24">
            <defs>
              <linearGradient id="oClipGradDesk" x1="0" y1="0" x2="24" y2="24">
                <stop offset="0%" stopColor="var(--acc1)" />
                <stop offset="50%" stopColor="var(--acc2)" />
                <stop offset="100%" stopColor="var(--acc3)" />
                <animateTransform
                  attributeName="gradientTransform"
                  type="rotate"
                  from="0 12 12"
                  to="360 12 12"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </linearGradient>
            </defs>
            <path
              d="M21.44 11.05l-8.49 8.49a6 6 0 01-8.49-8.49l8.49-8.49a4 4 0 015.66 5.66l-8.49 8.49a2 2 0 01-2.83-2.83l7.78-7.78"
              stroke="url(#oClipGradDesk)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <input
          className="o-input"
          placeholder="Escribe tu mensaje…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="Mensaje"
        />
        <button
          type="button"
          className="o-send-btn"
          onClick={handleSend}
          disabled={!input.trim()}
          aria-label="Enviar"
          title="Enviar"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            width="18"
            height="18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="o-send-icon"
          >
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ---------- Sheet de filtros (solo prioridad) ---------- */
function FilterSheet({
  isOpen,
  onClose,
  counts,
  cat,
  setCat,
}: {
  isOpen: boolean;
  onClose: () => void;
  counts: any;
  cat: Categoria;
  setCat: (v: Categoria) => void;
}) {
  const handleDragEnd = (_: any, info: PanInfo) => {
    if (info.offset.y > 100) onClose();
  };
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50">
          <motion.div
            className="absolute inset-0 bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="absolute bottom-0 left-0 right-0 o-card backdrop-blur-xl border-t border-border"
            drag="y"
            dragConstraints={{ top: 0, bottom: 500 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            role="dialog"
            aria-modal="true"
            aria-label="Filtros"
          >
            <div className="w-10 h-1.5 bg-muted-foreground/40 rounded-full mx-auto my-3" />
            <div className="px-4 pb-4 space-y-6">
              <div>
                <div className="text-sm font-semibold mb-2">Prioridad</div>
                <div className="grid grid-cols-3 gap-2">
                  <Chip active={cat === "urgente"} onClick={() => setCat("urgente")} tone="urgente">
                    Urgentes ({counts.u})
                  </Chip>
                  <Chip active={cat === "revision"} onClick={() => setCat("revision")} tone="revision">
                    Revisión ({counts.r})
                  </Chip>
                  <Chip active={cat === "neutra"} onClick={() => setCat("neutra")} tone="neutra">
                    Neutras ({counts.n})
                  </Chip>
                </div>
              </div>
            </div>
            <div className="px-4 pb-[calc(16px+env(safe-area-inset-bottom))]">
              <button onClick={onClose} className="o-btn-ghost w-full">
                Ver resultados
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function Chip({
  children,
  active,
  onClick,
  tone = "",
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  tone?: "urgente" | "revision" | "neutra" | "";
}) {
  const base = "px-3 py-2.5 rounded-xl border transition-all duration-200 text-[13px]";
  const tones: Record<string, string> = {
    urgente: "border-rose-400/50 text-rose-300",
    revision: "border-amber-400/50 text-amber-300",
    neutra: "border-slate-400/50 text-muted-foreground",
    "": "border-border text-muted-foreground",
  };
  const activeTones: Record<string, string> = {
    urgente: "bg-transparent border-rose-400 ring-2 ring-rose-400/50",
    revision: "bg-transparent border-amber-400 ring-2 ring-amber-400/50",
    neutra: "bg-transparent border-slate-400 ring-2 ring-slate-400/50",
    "": "bg-muted border-primary/60 ring-2 ring-primary/30",
  };
  const activeCls = active ? activeTones[tone] : "hover:bg-muted/50";
  return (
    <button onClick={onClick} className={[base, tones[tone], activeCls].join(" ")}>
      {children}
    </button>
  );
}
